import React from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';

/**
 * Canonical Modal component for the Muzado web client.
 *
 * Built on top of `react-modal`, but encapsulates the layout (header/body/
 * footer flexbox, sized via `size` prop) and the boilerplate (appElement,
 * className shape, overlay class shape) so consumers don't have to.
 *
 * Usage:
 *
 *   <Modal
 *     isOpen={open}
 *     onClose={() => setOpen(false)}
 *     size="md"
 *     title="Edit profile"
 *     footer={<button onClick={save}>Save</button>}
 *   >
 *     <FormFields />
 *   </Modal>
 *
 * Notes:
 *   - The body always becomes the scroll container — the modal itself never
 *     overflows the viewport. This is the layout-bug fix that motivated the
 *     refactor.
 *   - If `title` is provided we render a header. If `onClose` is provided AND
 *     `showCloseButton` is true (default) we render an "X" inside it.
 *   - `closeButtonDataTa` is exposed so legacy E2E selectors can be preserved
 *     during migration without leaking into the markup of new modals.
 */
const cx = (...classes) => classes.filter(Boolean).join(' ');

const SIZE_VALUES = ['sm', 'md', 'lg', 'xl', 'fullscreen'];

const Modal = ({
	isOpen,
	onClose,
	size = 'md',
	title,
	footer,
	children,
	contentLabel,
	className,
	bodyClassName,
	overlayClassName,
	closeOnOverlayClick = true,
	closeOnEsc = true,
	showCloseButton = true,
	closeButtonDataTa = 'modal-close',
	dataTa,
	...rest
}) => {
	const appElement = typeof window !== 'undefined'
		? window.document.querySelector('#app')
		: undefined;

	const showHeader = Boolean(title) || (showCloseButton && typeof onClose === 'function');

	return (
		<ReactModal
			isOpen={ isOpen }
			onRequestClose={ onClose }
			shouldCloseOnOverlayClick={ closeOnOverlayClick }
			shouldCloseOnEsc={ closeOnEsc }
			contentLabel={ contentLabel || (typeof title === 'string' ? title : 'Dialog') }
			appElement={ appElement }
			className={ {
				base: cx('mzd-modal', `mzd-modal--${size}`, className),
				afterOpen: 'mzd-modal--after-open',
				beforeClose: 'mzd-modal--before-close',
			} }
			overlayClassName={ {
				base: cx('mzd-modal__overlay', overlayClassName),
				afterOpen: 'mzd-modal__overlay--after-open',
				beforeClose: 'mzd-modal__overlay--before-close',
			} }
			{ ...rest }
		>
			<div className="mzd-modal__inner" data-ta={ dataTa }>
				{ showHeader && (
					<div className="mzd-modal__header">
						{ title && (
							<h2 className="mzd-modal__title">{ title }</h2>
						) }
						{ showCloseButton && typeof onClose === 'function' && (
							<button
								type="button"
								className="mzd-modal__close"
								aria-label="Close"
								onClick={ onClose }
								data-ta={ closeButtonDataTa }
							>
								&times;
							</button>
						) }
					</div>
				) }

				<div className={ cx('mzd-modal__body', bodyClassName) }>
					{ children }
				</div>

				{ footer && (
					<div className="mzd-modal__footer">
						{ footer }
					</div>
				) }
			</div>
		</ReactModal>
	);
};

Modal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func,
	size: PropTypes.oneOf(SIZE_VALUES),
	title: PropTypes.node,
	footer: PropTypes.node,
	children: PropTypes.node,
	contentLabel: PropTypes.string,
	className: PropTypes.string,
	bodyClassName: PropTypes.string,
	overlayClassName: PropTypes.string,
	closeOnOverlayClick: PropTypes.bool,
	closeOnEsc: PropTypes.bool,
	showCloseButton: PropTypes.bool,
	closeButtonDataTa: PropTypes.string,
	dataTa: PropTypes.string,
};

export default Modal;
